// includes/js-src/janus-engine/pl/layer-cells.ts — the small PHP cell helpers the PL pricer uses that the frozen corpus
// does not export: pl_cost_layer_entry_uuid (functions.php:2846), ability_entry_id / ability_entry_name
// (encounter_link_and_ability_builders.php:322/350), contains_trigger_key (functions.php:19613).
// Plan: plans/janus-c2-pl-in-browser-plan-2026-09-16.md §6.1.
import { readItemUuidSlot, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { readItemField, type ItemFieldDependencies, type ItemFieldRow } from '../compile/item-field-reader';
import { checkUuid } from './contract';
import { PlRefusal } from './refusal';

const arrayLike = (v: unknown): v is Record<string, unknown> | unknown[] => typeof v === 'object' && v !== null;
/** Own-property read of a wire object (inherited names such as `constructor` are never data). */
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Record<string, unknown>)[key] : undefined;
const phpTrim = (s: string): string => s.replace(/^[\x00\x09\x0A\x0B\x0D\x20]+|[\x00\x09\x0A\x0B\x0D\x20]+$/g, '');

/** pl_cost_layer_entry_uuid(): item_arr_uuid_slot first, then [0] (string or row), then item_id (string or [0]). */
export function plCostLayerEntryUuid(entry: unknown): string {
    const id = readItemUuidSlot(entry);
    if (id !== '') return id;
    if (!arrayLike(entry)) return '';
    const v = own(entry, '0') ?? null;
    if (typeof v === 'string') { const t = phpTrim(v); return checkUuid(t) ? t : ''; }
    if (arrayLike(v)) return readItemUuidSlot(v);
    let ii = own(entry, 'item_id') ?? null;
    if (arrayLike(ii)) ii = own(ii, '0') ?? '';
    if (typeof ii === 'string') { const t = phpTrim(ii); return checkUuid(t) ? t : ''; }
    return '';
}

export interface AbilityEntryDependencies extends ItemFieldDependencies {
    /** _ft_resolve_carrier_ref(id): the catalog / store definition, or null. */
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    /** get_item_id_name(id): the definition's item_name, or null when the browser cannot resolve it. */
    resolveItemName: (id: string) => unknown;
}

/** _ft_ability_entry_layer_src(): the row an entry's layers are read from (bare-uuid ref → definition; shell → definition; else the entry). */
export function abilityEntryLayerSrc(entry: Record<string, unknown> | unknown[], deps: AbilityEntryDependencies): ItemFieldRow {
    const hasItemArr = Object.prototype.hasOwnProperty.call(entry, 'item_arr');
    const ia = hasItemArr ? own(entry, 'item_arr') : (own(entry, 'item_id') ?? null);
    // PHP: `_ft_resolve_carrier_ref($id) ?? []` — the server's null is "no such item". The browser's null is "not held", which
    // must surface as a refusal, never as an empty row (Codex review).
    const resolveOrRefuse = (id: string): ItemFieldRow => { const def = deps.resolveCarrierRef(id); if (def === null) throw new PlRefusal('pl-def-missing:' + id.toUpperCase()); return def; };
    if (typeof ia === 'string' && ia !== '' && checkUuid(ia)) return resolveOrRefuse(ia);
    if (arrayLike(ia)) {
        const layers = own(ia, 'item_layer_types');
        const empty = layers === undefined || layers === null || (arrayLike(layers) && Object.keys(layers).length === 0) || layers === '' || layers === 0 || layers === false;
        if (empty) {
            const uuid = own(own(ia, 'items'), 'item_id');
            if (typeof uuid === 'string' && uuid !== '') return resolveOrRefuse(uuid);
        }
        return ia as ItemFieldRow;
    }
    return entry as ItemFieldRow;
}

/** ability_entry_id() */
export function abilityEntryId(entry: unknown, deps: AbilityEntryDependencies): string {
    if (typeof entry === 'string') return entry;
    if (!arrayLike(entry)) return '';
    const abilityId = own(entry, 'ability_id');
    if (typeof abilityId === 'string' && abilityId !== '') return abilityId;
    const itemArr = own(entry, 'item_arr');
    if (typeof itemArr === 'string' && itemArr !== '') return itemArr;
    const hasItemArr = Object.prototype.hasOwnProperty.call(entry, 'item_arr');
    const itemId = own(entry, 'item_id');
    if (!hasItemArr && typeof itemId === 'string' && itemId !== '') return itemId;
    const src = abilityEntryLayerSrc(entry, deps);
    const id = readItemField(src, 'item_id', deps) ?? readLayerListEntryUuid(src);
    return typeof id === 'string' ? id : '';
}

/** ability_entry_name() */
export function abilityEntryName(entry: unknown, deps: AbilityEntryDependencies): string {
    if (!arrayLike(entry)) return 'Unknown';
    const itemName = own(entry, 'item_name');
    if (typeof itemName === 'string' && itemName !== '') return itemName;
    const hasItemArr = Object.prototype.hasOwnProperty.call(entry, 'item_arr');
    const ref = hasItemArr ? own(entry, 'item_arr') : (own(entry, 'item_id') ?? null);
    if (typeof ref === 'string' && checkUuid(ref)) {
        const name = deps.resolveItemName(ref);
        return (typeof name === 'string' && name !== '') ? name : 'Unknown';
    }
    const src = abilityEntryLayerSrc(entry, deps);
    const name = readItemField(src, 'item_name', deps);
    return (typeof name === 'string' && name !== '') ? name : 'Unknown';
}

/** contains_trigger_key(): any key of the row or of its item_layer_types containing "trigger". */
export function containsTriggerKey(row: unknown): boolean {
    if (!arrayLike(row) || Object.keys(row).length === 0) return false;
    const keys = Object.keys(row);
    const layers = own(row, 'item_layer_types');
    if (arrayLike(layers)) keys.push(...Object.keys(layers));
    return keys.some(k => /trigger/.test(k));
}
